import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Container,
  Form,
  Pagination,
  Modal,
  Spinner,
  Alert,
  Dropdown,
  InputGroup,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import JsBarcode from "jsbarcode";
import { FaEbay } from "react-icons/fa";
import { SiShopify, SiWalmart } from "react-icons/si";
import {
  FaBox,
  FaSearch,
  FaPlus,
  FaBarcode,
  FaPrint,
  FaBoxes,
  FaSyncAlt,
  FaArchive,
  FaEye,
  FaEdit,
  FaTrash,
  FaLayerGroup,
  FaMagic,
} from "react-icons/fa";

const pageStyle = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)",
  color: "#e5e7eb",
};

const shellStyle = {
  maxWidth: 1900,
  margin: "0 auto",
};

const cardStyle = {
  background: "rgba(15, 23, 42, 0.86)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: 22,
  color: "#e5e7eb",
  boxShadow: "0 18px 45px rgba(0,0,0,0.28)",
  backdropFilter: "blur(10px)",
};

const cardSoftStyle = {
  background: "rgba(30, 41, 59, 0.82)",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  borderRadius: 20,
  color: "#e5e7eb",
};

const heroStyle = {
  background:
    "linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(34,197,94,0.20))",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 28,
  color: "#f8fafc",
  boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
};

const inputStyle = {
  background: "#020617",
  color: "#e5e7eb",
  border: "1px solid rgba(148, 163, 184, 0.35)",
  borderRadius: 12,
};

const mutedStyle = {
  color: "#94a3b8",
};

const pillStyle = {
  border: "1px solid rgba(148, 163, 184, 0.20)",
  background: "rgba(15, 23, 42, 0.72)",
  color: "#cbd5e1",
  borderRadius: 999,
  padding: "6px 11px",
  fontSize: 12,
  fontWeight: 700,
};

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatMoney = (value) => {
  const num = cleanNumber(value);

  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const platformOptions = [
  { value: "All", label: "All Platforms", icon: "🌎", color: "#cbd5e1" },
  { value: "Shopify", label: "Shopify", icon: "🛒", color: "#22c55e" },
  { value: "eBay", label: "eBay", icon: "🏷️", color: "#3b82f6" },
  { value: "Walmart", label: "Walmart", icon: "🏬", color: "#f59e0b" },
  { value: "Mercari", label: "Mercari", icon: "📦", color: "#8b5cf6" },
];

function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [syncDetails, setSyncDetails] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [archived, setArchived] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState("");
  const [showBinManageModal, setShowBinManageModal] = useState(false);
  const [selectedBinForEdit, setSelectedBinForEdit] = useState(null);
  const [newBin, setNewBin] = useState({
    location: "",
    maxLength: "",
    maxWidth: "",
    maxHeight: "",
    maxWeight: "",
    maxUnits: "",
  });
  const [binFormError, setBinFormError] = useState(null);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [barcodeImage, setBarcodeImage] = useState(null);
  const [currentSku, setCurrentSku] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [copiedTitleSku, setCopiedTitleSku] = useState(null);
  const [showProductBinsModal, setShowProductBinsModal] = useState(false);
  const [selectedProductForBins, setSelectedProductForBins] = useState(null);
  const [selectedProductBins, setSelectedProductBins] = useState([]);
  const [productBinsLoading, setProductBinsLoading] = useState(false);
  const [productBinsError, setProductBinsError] = useState(null);
  const [ebayListingSku, setEbayListingSku] = useState(null);
  // PhotoRoom preview-only workflow: Shopify products remain unchanged.
  const [backgroundSelected, setBackgroundSelected] = useState([]);
  const [backgroundModal, setBackgroundModal] = useState(false);
  const [backgroundQueue, setBackgroundQueue] = useState([]);
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundProcessing, setBackgroundProcessing] = useState(null);
  const [backgroundError, setBackgroundError] = useState("");
  const [backgroundPreviews, setBackgroundPreviews] = useState({});
  const [bulkBackgroundJob, setBulkBackgroundJob] = useState(null);
  const [bulkBackgroundBusy, setBulkBackgroundBusy] = useState(false);
  const [bulkBackgroundError, setBulkBackgroundError] = useState('');



  const navigate = useNavigate();
  const location = useLocation();

  const fetchInventory = useCallback(
    async (page = 1, search = "", options = {}) => {
      const { silent = false } = options;

      if (silent) {
        setTableRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        const response = await api.get(
          `/products?page=${page}&limit=${itemsPerPage}&archived=${archived ? 1 : 0}${
            search ? `&search=${encodeURIComponent(search)}` : ""
          }`
        );

        const data = response.data;

        console.log(
          "[Inventory] Fetched products:",
          data.products?.length,
          "Total pages:",
          data.totalPages,
          "Total items:",
          data.totalItems || data.products?.length
        );

        if (!Array.isArray(data.products)) {
          throw new Error("Invalid API response format");
        }

        setInventory(data.products);
        setTotalPages(data.totalPages || 1);
        setTotalItems(data.totalItems || data.products.length);
      } catch (error) {
        console.error("❌ Error fetching inventory:", error);
        setError("Failed to load inventory. Please try again.");
        setInventory([]);
        setTotalPages(1);
        setTotalItems(0);

        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } finally {
        setLoading(false);
        setTableRefreshing(false);
      }
    },
    [navigate, itemsPerPage, archived]
  );

  const fetchBins = useCallback(async () => {
    try {
      const response = await api.get("/bins");
      setBins(response.data);
    } catch (error) {
      console.error("❌ Error fetching bins:", error);

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearchQuery(searchQuery.trim());
  }, 350);

  return () => clearTimeout(timer);
}, [searchQuery]);

  useEffect(() => {
  const isInventoryPage =
    location.pathname.includes("/inventory/products") ||
    location.pathname === "/inventory";

  if (isInventoryPage) {
    fetchInventory(currentPage, debouncedSearchQuery);
  }

  fetchBins();

  const interval = setInterval(() => {
    if (isInventoryPage) {
      fetchInventory(currentPage, debouncedSearchQuery, { silent: true });
    }
  }, 300000);

  return () => clearInterval(interval);
}, [currentPage, debouncedSearchQuery, fetchInventory, fetchBins, location.pathname]);

  useEffect(() => {
    const state = location.state;

    if (state) {
      if (state.page) setCurrentPage(state.page);
      if (state.search) setSearchQuery(state.search);

      setTimeout(() => {
        window.scrollTo(0, state.scrollY || 0);
      }, 100);
    }
  }, [location.state]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
  setSearchQuery("");
  setDebouncedSearchQuery("");
  setCurrentPage(1);
};

  const handleItemsPerPageChange = (e) => {
  const newLimit = parseInt(e.target.value, 10);
  setItemsPerPage(newLimit);
  setCurrentPage(1);
};

  const handlePageJump = () => {
    const page = parseInt(pageInput, 10);

    if (page >= 1 && page <= totalPages) {
     setCurrentPage(page);
      setPageInput("");
     } else {
      alert(`Please enter a valid page number between 1 and ${totalPages}`);
    }
  };

  const handleImportEbay = async () => {
    setLoading(true);

    try {
      const response = await api.get("/ebay/import");
      alert(response.data.message);
      fetchInventory(currentPage, debouncedSearchQuery);
    } catch (error) {
      console.error("❌ Error importing from eBay:", error);
      alert("Failed to import from eBay.");

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const startBulkBackground = async () => {
    const skus = [...new Set(backgroundSelected.map(p => String(p).trim()).filter(Boolean))];
    if (!skus.length) return;
    if (!window.confirm(`Remove backgrounds from ALL Shopify photos of ${skus.length} selected products and publish the changes LIVE? Original files will be saved privately before replacement. PhotoRoom API credits will be used.`)) return;
    setBulkBackgroundBusy(true);
    setBulkBackgroundError('');
    try {
      const { data } = await api.post('/photo-backgrounds/bulk', { skus });
      setBulkBackgroundJob(data);
    } catch (err) {
      setBulkBackgroundError(err.response?.data?.error || err.message || 'Bulk processing could not start');
    } finally {
      setBulkBackgroundBusy(false);
    }
  };

  useEffect(() => {
    if (!bulkBackgroundJob?.id || bulkBackgroundJob.state !== 'RUNNING') return;
    let alive = true;
    let busy = false;
    const poll = async () => {
      if (busy) return;
      busy = true;
      try {
        const { data } = await api.get(`/photo-backgrounds/bulk/${encodeURIComponent(bulkBackgroundJob.id)}`);
        if (alive) setBulkBackgroundJob(data);
      } catch (err) {
        if (alive) setBulkBackgroundError(err.response?.data?.error || 'Could not fetch job status');
      } finally { busy = false; }
    };
    const interval = setInterval(poll, 2500);
    return () => { alive = false; clearInterval(interval); };
  }, [bulkBackgroundJob?.id, bulkBackgroundJob?.state]);

  const toggleBackgroundSelection = (sku, checked) => {
    setBackgroundSelected((prev) => checked
      ? Array.from(new Set([...prev, sku]))
      : prev.filter((item) => item !== sku));
  };

  const openBackgroundModal = (rows) => {
    const queue = rows.filter((p) => p.shopify_id && p.sku)
      .map((p) => ({ sku: String(p.sku), name: p.name || p.sku }));
    if (!queue.length) {
      alert("Select at least one Shopify-linked product with a SKU.");
      return;
    }
    setBackgroundQueue(queue);
    setBackgroundIndex(0);
    setBackgroundImages([]);
    setBackgroundError("");
    setBackgroundPreviews({});
    setBackgroundModal(true);
  };

  const backgroundCurrent = backgroundQueue[backgroundIndex];
  useEffect(() => {
    if (!backgroundModal || !backgroundCurrent?.sku) return;
    let active = true;
    setBackgroundLoading(true);
    setBackgroundImages([]);
    setBackgroundError("");
    api.get(`/photo-backgrounds/product/${encodeURIComponent(backgroundCurrent.sku)}/images`)
      .then(({ data }) => { if (active) setBackgroundImages(data.images || []); })
      .catch((err) => {
        if (active) setBackgroundError(err.response?.data?.error || "Could not load Shopify photos.");
      })
      .finally(() => { if (active) setBackgroundLoading(false); });
    return () => { active = false; };
  }, [backgroundModal, backgroundCurrent?.sku]);

  const previewBackgroundImage = async (mediaId) => {
    if (!backgroundCurrent?.sku || backgroundProcessing) return;
    const key = `${backgroundCurrent.sku}|${mediaId}`;
    setBackgroundProcessing(key);
    setBackgroundError("");
    try {
      const { data } = await api.post('/photo-backgrounds/preview', {
        sku: backgroundCurrent.sku, mediaId,
      });
      // Our shared API client already has /api as its baseURL.
      // Convert the server's absolute /api/... path to a relative API path.
      const previewPath = String(data.previewUrl || '').replace(/^\/api(?=\/)/, '');
      if (!previewPath.startsWith('/photo-backgrounds/previews/')) {
        throw new Error('Unexpected preview image path returned by server.');
      }
      const imageResponse = await api.get(previewPath, { responseType: 'blob' });
      const previewUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not load preview image'));
        reader.readAsDataURL(imageResponse.data);
      });
      setBackgroundPreviews((prev) => ({ ...prev, [key]: previewUrl }));
    } catch (err) {
      setBackgroundError(err.response?.data?.error || err.message || 'PhotoRoom preview failed.');
    } finally {
      setBackgroundProcessing(null);
    }
  };

  const handleAddProduct = () => navigate("/add-product");

  const handleEditClick = (sku) =>
    navigate(`/edit/${sku}`, {
      state: {
        from: location.pathname,
        page: currentPage,
        search: searchQuery,
        scrollY: window.scrollY,
      },
    });

    // List an existing Shopify product on eBay.
// Uses the existing eBay draft and publishing endpoints.
// Open the existing product in Editor for AI eBay preparation.
// Do not create an offer or publish from Inventory.
const handleListToEbay = async (rowProduct) => {
  const sku = String(rowProduct?.sku || "").trim();

  if (!sku || ebayListingSku) return;

  setEbayListingSku(sku);

  try {
    const response = await api.get(
      `/ebay/drafts/status/${encodeURIComponent(sku)}`
    );

    const product = response.data?.product;

    if (!product) {
      throw new Error("Could not verify eBay listing status.");
    }

    if (product.ebay_item_id || product.ebaySynced) {
      alert("This product is already linked to eBay.");
      return;
    }

    if (Number(rowProduct.stock) <= 0) {
      alert("This product has no available stock.");
      return;
    }

    navigate(`/edit/${encodeURIComponent(sku)}`, {
      state: {
        from: location.pathname,
        page: currentPage,
        search: searchQuery,
        scrollY: window.scrollY,
        prepareEbayWithAI: true,
      },
    });

  } catch (err) {
    console.error("[Inventory] AI eBay preparation failed:", err);

    alert(
      err.response?.data?.error ||
      err.message ||
      "Could not open eBay listing preparation."
    );
  } finally {
    setEbayListingSku(null);
  }
};

  const copyText = async (text, label = "Text") => {
    const cleanText = String(text || "").trim();

    if (!cleanText) {
      alert(`No ${label.toLowerCase()} found to copy.`);
      return false;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(cleanText);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = cleanText;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        textarea.style.top = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      return true;
    } catch (err) {
      console.error(`Failed to copy ${label}`, err);
      alert(`Failed to copy ${label.toLowerCase()}.`);
      return false;
    }
  };

  const handleCopyTitle = async (product) => {
    const title = String(product?.name || "").trim();
    const copyKey = String(product?.sku || product?.id || title || "");

    const copied = await copyText(title, "Title");

    if (copied) {
      setCopiedTitleSku(copyKey);

      window.setTimeout(() => {
        setCopiedTitleSku((current) => {
          return current === copyKey ? null : current;
        });
      }, 1400);
    }
  };

  const fetchSyncStatus = async (sku) => {
    try {
      const res = await api.get(`/products/sync-status/${sku}`);
      setSyncDetails(res.data);
      setShowSyncModal(true);
    } catch (err) {
      console.error("Error fetching sync status:", err);
      alert("Failed to load sync status.");
    }
  };

  const handleDelete = async (identifier) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await api.delete(`/products/${identifier}`);
      alert("Product deleted successfully!");
      fetchInventory(currentPage, debouncedSearchQuery);
    } catch (error) {
      console.error("❌ Error deleting product:", error);
      alert(`Failed to delete product: ${error.message}`);

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  const handleManageBins = () => setShowBinManageModal(true);

  const handleSaveBinConfig = async () => {
    const { location, maxLength, maxWidth, maxHeight, maxWeight, maxUnits } = newBin;

    if (!location.trim()) {
      setBinFormError("Bin location is required.");
      return;
    }

    if (maxLength === "" || parseFloat(maxLength) < 0) {
      setBinFormError("Max Length must be a non-negative number.");
      return;
    }

    if (maxWidth === "" || parseFloat(maxWidth) < 0) {
      setBinFormError("Max Width must be a non-negative number.");
      return;
    }

    if (maxHeight === "" || parseFloat(maxHeight) < 0) {
      setBinFormError("Max Height must be a non-negative number.");
      return;
    }

    if (maxWeight === "" || parseFloat(maxWeight) < 0) {
      setBinFormError("Max Weight must be a non-negative number.");
      return;
    }

    if (maxUnits === "" || parseInt(maxUnits, 10) < 0) {
      setBinFormError("Max Units must be a non-negative integer.");
      return;
    }

    const clean = location.trim().toUpperCase();
    const digits = clean.replace(/\D/g, "");

    if (!digits) {
      setBinFormError("Please enter a valid bin number.");
      return;
    }

    const normalized = `BIN-${digits.padStart(3, "0")}`;

    const binData = {
      location: digits,
      barcode: normalized,
      name: `Bin ${digits}`,
      maxLength: parseFloat(maxLength),
      maxWidth: parseFloat(maxWidth),
      maxHeight: parseFloat(maxHeight),
      maxWeight: parseFloat(maxWeight),
      maxUnits: parseInt(maxUnits, 10),
    };

    try {
      setBinFormError(null);

      if (selectedBinForEdit) {
        await api.put(`/bins/${selectedBinForEdit.id}`, binData);
      } else {
        await api.post("/bins", binData);
      }

      setShowBinManageModal(false);
      setSelectedBinForEdit(null);
      setNewBin({
        location: "",
        maxLength: "",
        maxWidth: "",
        maxHeight: "",
        maxWeight: "",
        maxUnits: "",
      });

      fetchBins();
      fetchInventory(currentPage, debouncedSearchQuery);
    } catch (error) {
      console.error("❌ Error saving bin:", error);

      const errorMessage = error.response?.data?.error || "Failed to save bin.";
      setBinFormError(errorMessage);

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  const handleEditBin = (bin) => {
    setSelectedBinForEdit(bin);
    setNewBin({
      location: bin.location,
      maxLength: bin.maxLength.toString(),
      maxWidth: bin.maxWidth.toString(),
      maxHeight: bin.maxHeight.toString(),
      maxWeight: bin.maxWeight.toString(),
      maxUnits: bin.maxUnits.toString(),
    });
    setShowBinManageModal(true);
  };

  const handleDeleteBin = async (bin_id) => {
    if (!window.confirm("Are you sure you want to delete this bin?")) return;

    try {
      await api.delete(`/bins/${bin_id}`);
      fetchBins();
    } catch (error) {
      console.error("❌ Error deleting bin:", error);
      alert("Failed to delete bin.");

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  const generateBarcode = async (sku) => {
    try {
      setBarcodeLoading(true);

      const response = await api.get(`/generate-barcode/${sku}`);
      const data = response.data;

      if (data.error) {
        alert("Error generating barcode: " + data.error);
        return;
      }

      setBarcodeImage(data.barcodeImage);
      setCurrentSku(sku);
      setBarcodeModalOpen(true);

      setInventory(
        inventory.map((product) =>
          product.sku === sku
            ? {
                ...product,
                barcode_image: data.barcodeImage,
              }
            : product
        )
      );
    } catch (error) {
      console.error("Error generating barcode:", error);
      alert("Failed to generate barcode");

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  const generateAllBarcodes = async () => {
    if (!window.confirm("Are you sure you want to generate barcodes for all products? This may take a while.")) {
      return;
    }

    try {
      setBarcodeLoading(true);

      const response = await api.post("/generate-all-barcodes");
      const data = response.data;

      if (data.error) {
        alert("Error generating barcodes: " + data.error);
        return;
      }

      alert(data.message);

      if (data.errors) {
        console.error("Errors during bulk barcode generation:", data.errors);
        alert("Some barcodes failed to generate. Check the console for details.");
      }

      fetchInventory(currentPage, debouncedSearchQuery);
    } catch (error) {
      console.error("Error generating all barcodes:", error);
      alert("Failed to generate barcodes");

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  const printBarcode = () => {
    if (!barcodeImage || !currentSku) {
      alert("No barcode available to print. Generate a barcode first.");
      return;
    }

    try {
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert("Popup blocked. Please allow popups and try again.");
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Print Product Barcode</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                .label {
                  width: 2.5in;
                  height: 1.5in;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  font-family: Arial, sans-serif;
                  padding: 10px;
                  font-weight: bold;
                  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
                }
                .barcode { width: 2in; height: 1in; }
                .sku { font-size: 18px; margin-bottom: 5px; }
                @page { size: 2.5in 1.5in; margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="label">
              <div class="sku">SKU: ${currentSku}</div>
              <canvas id="print-barcode" class="barcode"></canvas>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();

      JsBarcode(printWindow.document.getElementById("print-barcode"), currentSku, {
        format: "CODE128",
        width: 2,
        height: 50,
        displayValue: true,
        fontSize: 14,
        margin: 0,
      });

      printWindow.focus();
      printWindow.print();
      printWindow.close();

      console.log(`[${new Date().toISOString()}] [Inventory] Printed barcode for SKU ${currentSku}`);
    } catch (error) {
      console.error(`Failed to print barcode for SKU ${currentSku}:`, error);
      alert("Failed to print barcode.");
    }
  };

  const printAllBarcodes = async () => {
    try {
      const response = await api.get(
        `/products?page=${currentPage}&limit=${itemsPerPage}${debouncedSearchQuery ? `&search=${encodeURIComponent(debouncedSearchQuery)}` : ""}`
      );

      const data = response.data;
      const productsWithBarcodes = data.products.filter((product) => product.sku);

      if (productsWithBarcodes.length === 0) {
        alert("No barcodes available to print. Generate barcodes first.");
        return;
      }

      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert("Popup blocked. Please allow popups and try again.");
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Print All Product Barcodes</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
                .label {
                  width: 2.5in;
                  height: 1.5in;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  font-family: Arial, sans-serif;
                  padding: 10px;
                  font-weight: bold;
                  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
                  page-break-after: always;
                }
                .barcode { width: 2in; height: 1in; }
                .sku { font-size: 18px; margin-bottom: 5px; }
                @page { size: 2.5in 1.5in; margin: 0; }
              }
            </style>
          </head>
          <body>
            ${productsWithBarcodes
              .map(
                (product) => `
              <div class="label">
                <div class="sku">SKU: ${product.sku}</div>
                <canvas id="barcode-${product.sku}" class="barcode"></canvas>
              </div>
            `
              )
              .join("")}
          </body>
        </html>
      `);

      printWindow.document.close();

      productsWithBarcodes.forEach((product) => {
        const canvas = printWindow.document.getElementById(`barcode-${product.sku}`);

        if (canvas) {
          try {
            JsBarcode(canvas, product.sku, {
              format: "CODE128",
              width: 2,
              height: 50,
              displayValue: true,
              fontSize: 14,
              margin: 0,
            });
          } catch (error) {
            console.error(`Failed to generate barcode for SKU ${product.sku} in print window:`, error);
          }
        }
      });

      printWindow.focus();
      printWindow.print();
      printWindow.close();

      console.log(`[${new Date().toISOString()}] [Inventory] Printed all barcodes`);
    } catch (error) {
      console.error("Error printing barcodes:", error);
      alert("Failed to print barcodes");

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  const getImageUrl = (image) => {
    if (!image) return "No Image";

    const baseUrl = process.env.REACT_APP_API_URL || "https://thornsdashboard.com";

    return image.startsWith("http") ? image : `${baseUrl}${image}`;
  };

  const renderPlatformIcons = (platformString) => {
    if (!platformString) return <span style={mutedStyle}>—</span>;

    const platforms = platformString.split(",").map((p) => p.trim());

    const iconBase = {
      opacity: 0.9,
      transition: "all 0.15s ease",
      cursor: "pointer",
    };

    const withHover = (Icon, label, color) => (
      <Icon
        key={label}
        size={21}
        title={label}
        style={{
          ...iconBase,
          color,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.16)";
          e.currentTarget.style.opacity = "1";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.opacity = "0.9";
        }}
      />
    );

    return (
      <div className="d-flex gap-2 align-items-center">
        {platforms.includes("eBay") && withHover(FaEbay, "eBay", "#3b82f6")}
        {platforms.includes("Shopify") && withHover(SiShopify, "Shopify", "#22c55e")}
        {platforms.includes("Walmart") && withHover(SiWalmart, "Walmart", "#f59e0b")}
        {platforms.includes("Mercari") && withHover(FaBox, "Mercari", "#8b5cf6")}
      </div>
    );
  };

  const normalizeProductBins = (product) => {
  const possibleBins =
    product?.bins ||
    product?.inventory_bins ||
    product?.inventoryBins ||
    product?.product_bins ||
    product?.productBins ||
    [];

  if (Array.isArray(possibleBins) && possibleBins.length > 0) {
    return possibleBins
      .map((bin) => ({
        bin_id: bin.bin_id || bin.binId || bin.id,
        location: bin.location || bin.bin_location || bin.binLocation || bin.name,
        name: bin.name,
        barcode: bin.barcode,
        quantity: bin.quantity || bin.qty || bin.stock || 0,
      }))
      .filter((bin) => bin.bin_id || bin.location);
  }

  if (product?.bin_id || product?.bin_location) {
    return [
      {
        bin_id: product.bin_id || null,
        location: product.bin_location || product.bin_id || "Unknown",
        name: product.bin_location || product.bin_id || "Unknown",
        barcode: null,
        quantity: product.stock || 0,
      },
    ];
  }

  return [];
};

const handleOpenProductBins = async (product) => {
  try {
    setSelectedProductForBins(product);
    setSelectedProductBins([]);
    setProductBinsError(null);
    setProductBinsLoading(true);
    setShowProductBinsModal(true);

    const skuOrId = product.sku || product.id;

    let detailedProduct = null;

    try {
      const res = await api.get(`/products/${encodeURIComponent(skuOrId)}`);
      detailedProduct = res.data;
    } catch (err) {
      console.warn("[Inventory] Could not fetch product bin details, using row data:", err.message);
    }

    const binsFromDetail = normalizeProductBins(detailedProduct || {});
    const binsFromRow = normalizeProductBins(product);

    const finalBins = binsFromDetail.length > 0 ? binsFromDetail : binsFromRow;

    setSelectedProductBins(finalBins);

    if (finalBins.length === 0) {
      setProductBinsError("No bin locations found for this product.");
    }
  } catch (err) {
    console.error("[Inventory] Failed to open product bins:", err);
    setProductBinsError(err.response?.data?.error || err.message || "Failed to load bins.");
  } finally {
    setProductBinsLoading(false);
  }
};

  const filteredInventory = inventory.filter((product) => {
    const platformMatch =
      selectedPlatform === "All" ||
      (product.platform && product.platform.includes(selectedPlatform));

    const searchMatch = true;

    const archivedMatch = archived ? Number(product.archived) === 1 : Number(product.archived) !== 1;

    return platformMatch && searchMatch && archivedMatch;
  });

  const pageStockTotal = useMemo(() => {
    return filteredInventory.reduce((sum, product) => sum + cleanNumber(product.stock), 0);
  }, [filteredInventory]);

  const pageValueTotal = useMemo(() => {
    return filteredInventory.reduce(
      (sum, product) => sum + cleanNumber(product.price) * cleanNumber(product.stock),
      0
    );
  }, [filteredInventory]);

  const productsWithoutBins = useMemo(() => {
    return filteredInventory.filter((product) => !product.bin_id || String(product.bin_id) === "999").length;
  }, [filteredInventory]);

  const selectedPlatformMeta =
    platformOptions.find((platform) => platform.value === selectedPlatform) || platformOptions[0];

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <Pagination className="justify-content-center mt-3 inventory-pagination">
        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
        <Pagination.Prev
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        />

        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
          .map((page, _, filtered) => (
            <React.Fragment key={page}>
              {filtered.indexOf(page) > 0 && page - filtered[filtered.indexOf(page) - 1] > 1 && (
                <Pagination.Ellipsis disabled />
              )}
              <Pagination.Item active={page === currentPage} onClick={() => setCurrentPage(page)}>
                {page}
              </Pagination.Item>
            </React.Fragment>
          ))}

        <Pagination.Next
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  const MiniMetric = ({ label, value, color = "#e5e7eb", icon }) => {
    return (
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 16,
          background: "rgba(2, 6, 23, 0.45)",
          border: "1px solid rgba(148, 163, 184, 0.12)",
        }}
      >
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 3 }}>
          {icon && <span className="me-1">{icon}</span>}
          {label}
        </div>
        <div style={{ color, fontWeight: 850 }}>{value}</div>
      </div>
    );
  };

  const StatCard = ({ label, value, subtext, accent = "#38bdf8", icon = "📦", valueColor }) => {
    return (
      <div className="col-md-6 col-xl-3 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
              pointerEvents: "none",
            }}
          />

          <div className="p-3 p-lg-4 position-relative">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div
                  style={{
                    color: "#94a3b8",
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  {label}
                </div>

                <div
                  className="mt-2"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    fontWeight: 900,
                    color: valueColor || "#f8fafc",
                  }}
                >
                  {value}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  background: `${accent}22`,
                  border: `1px solid ${accent}35`,
                  fontSize: 22,
                }}
              >
                {icon}
              </div>
            </div>

            {subtext && (
              <div className="mt-2" style={{ color: "#94a3b8", fontSize: 13 }}>
                {subtext}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading && inventory.length === 0) {
    return (
      <div style={pageStyle}>
        <Container fluid className="py-4" style={shellStyle}>
          <div className="p-4" style={cardStyle}>
            <div className="d-flex align-items-center gap-3">
              <Spinner animation="border" variant="info" />
              <div>
                <h4 className="mb-1" style={{ color: "#f8fafc" }}>
                  Loading Inventory
                </h4>
                <p className="mb-0" style={mutedStyle}>
                  Pulling products, bins, platform status, and barcode data.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <style>
        {`
          .inventory-dark input,
          .inventory-dark select,
          .inventory-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .inventory-dark input::placeholder,
          .inventory-dark textarea::placeholder {
            color: #64748b !important;
          }

          .inventory-dark input:focus,
          .inventory-dark select:focus,
          .inventory-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.18) !important;
            border-color: rgba(56, 189, 248, 0.75) !important;
          }

          .inventory-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .inventory-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .inventory-dark .dropdown-menu {
            background: #0f172a;
            border: 1px solid rgba(148, 163, 184, 0.18);
            border-radius: 16px;
            box-shadow: 0 18px 45px rgba(0,0,0,0.35);
          }

          .inventory-dark .dropdown-item {
            color: #e5e7eb;
            font-weight: 700;
          }

          .inventory-dark .dropdown-item:hover {
            background: rgba(59,130,246,0.16);
            color: #f8fafc;
          }

          .inventory-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
            margin-bottom: 0;
          }

          .inventory-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            white-space: nowrap;
            vertical-align: middle;
          }

          .inventory-dark .table tbody td {
            vertical-align: middle;
            border-color: rgba(148, 163, 184, 0.10);
            white-space: nowrap;
          }

          .inventory-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48);
          }

          .inventory-dark .pagination .page-link {
            background: rgba(15, 23, 42, 0.88);
            border-color: rgba(148, 163, 184, 0.18);
            color: #cbd5e1;
            font-weight: 700;
          }

          .inventory-dark .pagination .page-item.active .page-link {
            background: #2563eb;
            border-color: #2563eb;
            color: #ffffff;
          }

          .inventory-dark .pagination .page-item.disabled .page-link {
            background: rgba(15, 23, 42, 0.55);
            border-color: rgba(148, 163, 184, 0.10);
            color: #64748b;
          }

          .inventory-modal .modal-content {
            background: #0f172a;
            border: 1px solid rgba(148, 163, 184, 0.18);
            border-radius: 22px;
            color: #e5e7eb;
            box-shadow: 0 24px 70px rgba(0,0,0,0.45);
          }

          .inventory-modal .modal-header {
            border-bottom: 1px solid rgba(148, 163, 184, 0.14);
          }

          .inventory-modal .modal-footer {
            border-top: 1px solid rgba(148, 163, 184, 0.14);
          }

          .inventory-modal .btn-close {
            filter: invert(1) grayscale(100%) brightness(200%);
          }

          .inventory-product-image {
            width: 62px;
            height: 62px;
            object-fit: contain;
            border-radius: 14px;
            border: 1px solid rgba(148, 163, 184, 0.22);
            background: rgba(2, 6, 23, 0.60);
            padding: 4px;
          }

          .inventory-barcode-image {
            width: 118px;
            height: 52px;
            object-fit: contain;
            border-radius: 12px;
            border: 1px solid rgba(148, 163, 184, 0.16);
            background: rgba(255, 255, 255, 0.92);
            padding: 4px;
          }
        `}
      </style>

      <Container fluid className="py-4 inventory-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: "#bfdbfe", borderColor: "rgba(59,130,246,0.38)" }}>
                  Inventory
                </span>
                <span style={{ ...pillStyle, color: "#bbf7d0", borderColor: "rgba(34,197,94,0.38)" }}>
                  {archived ? "Archived Products" : "Active Products"}
                </span>
                <span style={{ ...pillStyle, color: selectedPlatformMeta.color, borderColor: "rgba(148,163,184,0.28)" }}>
                  {selectedPlatformMeta.icon} {selectedPlatformMeta.label}
                </span>
                <span style={{ ...pillStyle, color: "#fef3c7", borderColor: "rgba(245,158,11,0.38)" }}>
                  Bins + Barcodes
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: "#f8fafc",
                  fontWeight: 950,
                  letterSpacing: "-0.04em",
                  fontSize: "clamp(2rem, 4vw, 4.35rem)",
                  lineHeight: 0.95,
                }}
              >
                Inventory Control Center
              </h1>

              <p className="mb-0" style={{ color: "#cbd5e1", maxWidth: 940, fontSize: 17 }}>
                Search products, manage bins, import eBay inventory, generate barcodes, check platform sync,
                and jump straight into the editor without losing your place.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 330,
              }}
            >
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <div style={{ color: "#94a3b8", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 }}>
                    Current View
                  </div>

                  <div style={{ color: "#f8fafc", fontSize: 26, fontWeight: 900 }}>
                    Page {currentPage} / {totalPages}
                  </div>
                </div>

                <Dropdown>
                  <Dropdown.Toggle variant="outline-light" id="dropdown-inventory">
                    <FaEye className="me-2" />
                    View
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end">
                    <Dropdown.Item as="button" onClick={() => navigate("/inventory/products")}>
                      Products
                    </Dropdown.Item>
                    <Dropdown.Item as="button" onClick={() => navigate("/inventory/drafts")}>
                      Drafts
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <Row className="g-2">
                <Col xs={6}>
                  <MiniMetric label="Total Items" value={totalItems.toLocaleString()} color="#38bdf8" />
                </Col>
                <Col xs={6}>
                  <MiniMetric label="Showing" value={filteredInventory.length.toLocaleString()} color="#c4b5fd" />
                </Col>
              </Row>

              <div className="d-grid mt-3">
                <Button
                  variant="info"
                  onClick={() => fetchInventory(currentPage, debouncedSearchQuery, { silent: true })}
                  disabled={tableRefreshing || loading}
                >
                  {tableRefreshing ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <FaSyncAlt className="me-2" />
                      Refresh Inventory
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            style={{
              background: "rgba(127,29,29,0.70)",
              border: "1px solid rgba(248,113,113,0.35)",
              color: "#fee2e2",
              borderRadius: 18,
            }}
          >
            <strong>Inventory could not load.</strong>
            <div>{error}</div>
            <Button size="sm" variant="light" className="mt-2" onClick={() => fetchInventory(currentPage, searchQuery)}>
              Retry
            </Button>
          </Alert>
        )}

        <Row className="mb-4">
          <StatCard
            label="Total Items"
            value={totalItems.toLocaleString()}
            subtext="Matching current active/archive mode"
            accent="#38bdf8"
            icon="📦"
          />

          <StatCard
            label="Showing This Page"
            value={filteredInventory.length.toLocaleString()}
            subtext={`Filtered from ${inventory.length.toLocaleString()} loaded rows`}
            accent="#a855f7"
            icon="📋"
          />

          <StatCard
            label="Page Stock"
            value={pageStockTotal.toLocaleString()}
            subtext="Stock quantity from visible rows"
            accent="#22c55e"
            icon="✅"
            valueColor="#86efac"
          />

          <StatCard
            label="Page Value"
            value={formatMoney(pageValueTotal)}
            subtext={`${productsWithoutBins} item${productsWithoutBins === 1 ? "" : "s"} need bin review`}
            accent="#f59e0b"
            icon="💰"
            valueColor="#fbbf24"
          />
        </Row>

        <div className="p-4 mb-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1" style={{ color: "#f8fafc", fontWeight: 900 }}>
                Actions
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Add products, import from eBay, manage bins, barcodes, and PhotoRoom previews.
              </p>
            </div>

            <Form.Check
              type="switch"
              id="toggle-archived"
              label={archived ? "Showing Archived" : "Showing Active"}
              checked={archived}
              onChange={() => {
                setArchived(!archived);
                setCurrentPage(1);
              }}
              style={{ color: "#cbd5e1", fontWeight: 800 }}
            />
          </div>

          {bulkBackgroundError && <Alert variant="danger">{bulkBackgroundError}</Alert>}
          {bulkBackgroundJob && (
            <Alert variant="info" className="mb-3">
              <strong>Photo backgrounds: {bulkBackgroundJob.state}</strong>{' — '}
              {bulkBackgroundJob.productsDone}/{bulkBackgroundJob.totalProducts} products finished, {bulkBackgroundJob.succeeded} images submitted to Shopify,
              {' '}{bulkBackgroundJob.failed} failed, {bulkBackgroundJob.skipped} duplicates skipped.
              {bulkBackgroundJob.current && <div>Processing SKU: {bulkBackgroundJob.current}</div>}
              {Array.isArray(bulkBackgroundJob.errors) && bulkBackgroundJob.errors.length > 0 && (
                <div className="mt-2" style={{maxHeight:130, overflow:'auto'}}>
                  {bulkBackgroundJob.errors.map((e,i)=><div key={i}>{e.sku || 'Job'}: {e.error}</div>)}
                </div>
              )}
            </Alert>
          )}
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-info"
              disabled={!filteredInventory.some((p) => backgroundSelected.includes(p.sku) && p.shopify_id)}
              onClick={() => openBackgroundModal(filteredInventory.filter((p) => backgroundSelected.includes(p.sku)))}
            >
              <FaMagic className="me-2" />
              Remove Background ({filteredInventory.filter((p) => backgroundSelected.includes(p.sku) && p.shopify_id).length})
            </Button>
            <Button variant="success" disabled={bulkBackgroundBusy || bulkBackgroundJob?.state === 'RUNNING' || backgroundSelected.length === 0}
              onClick={startBulkBackground}>
              {bulkBackgroundBusy ? 'Starting...' : 'Bulk Remove & Apply to Shopify (All Photos)'}
            </Button>
            <Button variant="primary" onClick={handleAddProduct}>
              <FaPlus className="me-2" />
              Add Product
            </Button>

            <Button variant="success" onClick={handleImportEbay} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Importing...
                </>
              ) : (
                <>
                  <FaEbay className="me-2" />
                  Import from eBay
                </>
              )}
            </Button>

            <Button variant="info" onClick={handleManageBins}>
              <FaBoxes className="me-2" />
              Manage Bins
            </Button>

            <Button variant="secondary" onClick={generateAllBarcodes} disabled={barcodeLoading}>
              {barcodeLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Generating...
                </>
              ) : (
                <>
                  <FaBarcode className="me-2" />
                  Generate All Barcodes
                </>
              )}
            </Button>

            <Button variant="outline-light" onClick={printAllBarcodes} disabled={barcodeLoading}>
              <FaPrint className="me-2" />
              Print All Barcodes
            </Button>
          </div>
        </div>

        <div className="p-4 mb-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1" style={{ color: "#f8fafc", fontWeight: 900 }}>
                Filters
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Search by SKU, name, UPC, or bin. Filter by platform and adjust page size.
              </p>
            </div>

            <span style={{ ...pillStyle, color: selectedPlatformMeta.color }}>
              {selectedPlatformMeta.icon} {selectedPlatformMeta.label}
            </span>
          </div>

          <Row className="g-3 align-items-end">
            <Col xl={3} lg={4} md={6}>
              <Form.Label>Platform</Form.Label>
              <Form.Select
                style={inputStyle}
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
              >
                {platformOptions.map((platform) => (
                  <option key={platform.value} value={platform.value}>
                    {platform.icon} {platform.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xl={4} lg={5} md={6}>
              <Form.Label>Search Inventory</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    background: "#020617",
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    color: "#94a3b8",
                    borderRadius: "12px 0 0 12px",
                  }}
                >
                  <FaSearch />
                </InputGroup.Text>

                <Form.Control
                  type="text"
                  placeholder="Search SKU, name, UPC, or bin..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{
                    ...inputStyle,
                    borderRadius: 0,
                  }}
                />

                <Button variant="outline-light" onClick={handleClearSearch}>
                  Clear
                </Button>
              </InputGroup>
            </Col>

            <Col xl={2} lg={3} md={4}>
              <Form.Label>Items Per Page</Form.Label>
              <Form.Select style={inputStyle} value={itemsPerPage} onChange={handleItemsPerPageChange}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
            </Col>

            <Col xl={3} lg={4} md={8}>
              <Form.Label>Jump to Page</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  placeholder={`1-${totalPages}`}
                  style={inputStyle}
                />

                <Button variant="primary" onClick={handlePageJump}>
                  Go
                </Button>
              </InputGroup>
            </Col>
          </Row>
        </div>

        <div className="p-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1" style={{ color: "#f8fafc", fontWeight: 900 }}>
                Products
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Total Items: {totalItems.toLocaleString()} · Showing {filteredInventory.length.toLocaleString()} on page {currentPage} of {totalPages}
              </p>
            </div>

            {tableRefreshing && (
              <span style={{ ...pillStyle, color: "#bfdbfe" }}>
                <Spinner size="sm" className="me-2" />
                Refreshing
              </span>
            )}
          </div>

          <div className="table-responsive">
            <Table hover responsive className="align-middle">
              <thead>
                <tr>
                  <th>
                    <Form.Check type="checkbox" aria-label="Select all Shopify products on this page"
                      checked={filteredInventory.some((p) => p.shopify_id && p.sku) && filteredInventory.filter((p) => p.shopify_id && p.sku).every((p) => backgroundSelected.includes(p.sku))}
                      onChange={(e) => {
                        const skus = filteredInventory.filter((p) => p.shopify_id && p.sku).map((p) => p.sku);
                        setBackgroundSelected((prev) => e.target.checked
                          ? Array.from(new Set([...prev, ...skus]))
                          : prev.filter((sku) => !skus.includes(sku)));
                      }}
                    />
                  </th>
                  <th>SKU</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">Stock</th>
                  <th>Platform</th>
                  <th>Bin</th>
                  <th>Sync</th>
                  <th>Barcode</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredInventory.length > 0 ? (
                  filteredInventory.map((product) => {
                    const copiedKey = String(product.sku || product.id || product.name || "");

                    return (
                      <tr key={product.sku || `id-${product.id}`}>
                        <td>
                          <Form.Check type="checkbox" aria-label={`Select ${product.sku}`}
                            disabled={!product.shopify_id || !product.sku}
                            checked={backgroundSelected.includes(product.sku)}
                            onChange={(e) => toggleBackgroundSelection(product.sku, e.target.checked)}
                          />
                        </td>
                        <td>
                          <strong style={{ color: "#f8fafc" }}>{product.sku || "N/A"}</strong>
                        </td>

                        <td>
                          {product.image ? (
                            <img
                              src={getImageUrl(product.image)}
                              alt={product.name || product.sku}
                              className="inventory-product-image"
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center inventory-product-image"
                              style={{ color: "#64748b", fontSize: 12 }}
                            >
                              No Image
                            </div>
                          )}
                        </td>

                        <td style={{ minWidth: 280, whiteSpace: "normal" }}>
                          {product.name ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleCopyTitle(product)}
                                title="Click to copy title"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  padding: 0,
                                  margin: 0,
                                  color: "#e5e7eb",
                                  fontWeight: 800,
                                  textAlign: "left",
                                  cursor: "pointer",
                                  whiteSpace: "normal",
                                  lineHeight: 1.25,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = "#93c5fd";
                                  e.currentTarget.style.textDecoration = "underline";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = "#e5e7eb";
                                  e.currentTarget.style.textDecoration = "none";
                                }}
                              >
                                {product.name}
                              </button>

                              {copiedTitleSku === copiedKey && (
                                <div
                                  style={{
                                    color: "#86efac",
                                    fontSize: 11,
                                    fontWeight: 900,
                                    marginTop: 4,
                                  }}
                                >
                                  Copied title ✓
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ color: "#64748b", fontWeight: 800 }}>No Name</div>
                          )}

                          {product.upc && (
                            <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 3 }}>
                              UPC: {product.upc}
                            </div>
                          )}
                        </td>

                        <td className="text-end">{formatMoney(product.price)}</td>

                        <td className="text-end">
                          <Badge
                            bg=""
                            style={{
                              background:
                                cleanNumber(product.stock) > 0
                                  ? "rgba(34,197,94,0.16)"
                                  : "rgba(239,68,68,0.16)",
                              color: cleanNumber(product.stock) > 0 ? "#86efac" : "#fca5a5",
                              border:
                                cleanNumber(product.stock) > 0
                                  ? "1px solid rgba(34,197,94,0.28)"
                                  : "1px solid rgba(239,68,68,0.28)",
                              borderRadius: 999,
                              padding: "7px 10px",
                            }}
                          >
                            {cleanNumber(product.stock).toLocaleString()}
                          </Badge>
                        </td>

                        <td>{renderPlatformIcons(product.platform)}</td>

                        <td>
  {product.bin_id || product.bin_location ? (
    <button
      type="button"
      onClick={() => handleOpenProductBins(product)}
      title="Click to view all bins for this product"
      style={{
        ...pillStyle,
        color: String(product.bin_id) === "999" ? "#fbbf24" : "#bbf7d0",
        cursor: "pointer",
        border:
          String(product.bin_id) === "999"
            ? "1px solid rgba(251,191,36,0.35)"
            : "1px solid rgba(34,197,94,0.35)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.04)";
        e.currentTarget.style.background = "rgba(59,130,246,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.background = "rgba(15, 23, 42, 0.72)";
      }}
    >
      <FaArchive className="me-1" />
      {product.bin_location || product.bin_id}
    </button>
  ) : (
    <Button
      variant="outline-secondary"
      size="sm"
      onClick={() => handleOpenProductBins(product)}
    >
      View Bins
    </Button>
  )}
</td>

                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => fetchSyncStatus(product.sku)}
                          >
                            <FaLayerGroup className="me-1" />
                            View Sync
                          </Button>
                        </td>

                        <td>
                          {product.barcode_image ? (
                            <img
                              src={getImageUrl(product.barcode_image)}
                              alt={`Barcode for ${product.sku}`}
                              className="inventory-barcode-image"
                            />
                          ) : (
                            <span style={mutedStyle}>No Barcode</span>
                          )}
                        </td>

                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              variant="outline-light"
                              size="sm"
                              onClick={() => generateBarcode(product.sku || product.id)}
                              disabled={barcodeLoading}
                            >
                              <FaBarcode className="me-1" />
                              Barcode
                            </Button>

                            <Button
  variant="warning"
  size="sm"
  onClick={() => handleEditClick(product.sku || product.id)}
>
  <FaEdit className="me-1" />
  Edit
</Button>

{product.ebay_item_id ? (
  <Button
    variant="outline-primary"
    size="sm"
    onClick={() =>
      window.open(
        `https://www.ebay.com/itm/${encodeURIComponent(
          product.ebay_item_id
        )}`,
        "_blank",
        "noopener,noreferrer"
      )
    }
  >
    <FaEbay className="me-1" />
    View eBay
  </Button>
) : product.shopify_id && !archived ? (
  <Button
    variant="outline-success"
    size="sm"
    disabled={
      Boolean(ebayListingSku) ||
      cleanNumber(product.stock) <= 0
    }
    onClick={() => handleListToEbay(product)}
    title={
      cleanNumber(product.stock) <= 0
        ? "Out of stock"
        : "Publish on eBay"
    }
  >
    {ebayListingSku === product.sku ? (
      <Spinner
        animation="border"
        size="sm"
        className="me-1"
      />
    ) : (
      <FaEbay className="me-1" />
    )}

    {ebayListingSku === product.sku
      ? "Listing..."
      : "List to eBay"}
  </Button>
) : null}

                            {product.shopify_id && product.sku && (
                              <Button size="sm" variant="outline-info"
                                onClick={() => openBackgroundModal([product])}>
                                <FaMagic className="me-1" /> Background
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(product.sku || product.id)}
                            >
                              <FaTrash className="me-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="text-center py-5">
                      <div style={{ color: "#f8fafc", fontWeight: 900, fontSize: 18 }}>
                        No products available.
                      </div>
                      <div style={mutedStyle}>
                        Try clearing the search, changing platform, or switching archive mode.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {renderPagination()}
        </div>

        {/* Private PhotoRoom previews only. Publishing is intentionally not available yet. */}
        <Modal show={backgroundModal} onHide={() => !backgroundProcessing && setBackgroundModal(false)}
          size="xl" centered className="inventory-modal">
          <Modal.Header closeButton={!backgroundProcessing}>
            <Modal.Title>Remove Background — Shopify Photos</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info">
              Preview only. Original Shopify listings and photos are NOT changed.
              PhotoRoom processing may consume API credits.
            </Alert>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <strong>{backgroundCurrent?.name || 'Product'} ({backgroundCurrent?.sku})</strong>
              <span>{backgroundIndex + 1} of {backgroundQueue.length} selected products</span>
            </div>
            {backgroundError && <Alert variant="danger">{backgroundError}</Alert>}
            {backgroundLoading ? (
              <div className="py-4 text-center"><Spinner animation="border" /> Loading Shopify photos...</div>
            ) : backgroundImages.length === 0 ? (
              <div style={mutedStyle}>No Shopify images available for this product.</div>
            ) : (
              <Row className="g-3">
                {backgroundImages.map((photo) => {
                  const key = `${backgroundCurrent?.sku}|${photo.id}`;
                  return (
                    <Col key={photo.id} xs={12} md={6} lg={4}>
                      <div className="p-3 h-100" style={cardSoftStyle}>
                        <div className="mb-2" style={{ background: '#ffffff', borderRadius: 12, padding: 8, minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={photo.url} alt={photo.alt || 'Shopify original'} style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                        </div>
                        <div className="mb-2" style={{ fontWeight: 700 }}>Original photo</div>
                        {backgroundPreviews[key] && (
                          <>
                            <div style={{ background: '#050505', borderRadius: 12, padding: 8, minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img src={backgroundPreviews[key]} alt="Transparent preview on black" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                            </div>
                            <div className="mt-2 mb-2" style={{ color: '#86efac' }}>Transparent preview on black</div>
                          </>
                        )}
                        <Button variant="info" className="w-100" disabled={Boolean(backgroundProcessing)}
                          onClick={() => previewBackgroundImage(photo.id)}>
                          {backgroundProcessing === key ? <><Spinner size="sm" className="me-2" />Processing...</> : backgroundPreviews[key] ? 'Regenerate Preview' : 'Generate Preview'}
                        </Button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button variant="secondary" disabled={Boolean(backgroundProcessing)} onClick={() => setBackgroundModal(false)}>Close</Button>
            <div className="d-flex gap-2">
              <Button variant="outline-light" disabled={backgroundIndex === 0 || Boolean(backgroundProcessing)}
                onClick={() => setBackgroundIndex((i) => i - 1)}>Previous product</Button>
              <Button variant="primary" disabled={backgroundIndex >= backgroundQueue.length - 1 || Boolean(backgroundProcessing)}
                onClick={() => setBackgroundIndex((i) => i + 1)}>Next product</Button>
            </div>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showBinManageModal}
          onHide={() => setShowBinManageModal(false)}
          size="xl"
          centered
          className="inventory-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{selectedBinForEdit ? "Edit Bin" : "Add New Bin"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {binFormError && (
              <Alert
                style={{
                  background: "rgba(127,29,29,0.70)",
                  border: "1px solid rgba(248,113,113,0.35)",
                  color: "#fee2e2",
                  borderRadius: 16,
                }}
              >
                {binFormError}
              </Alert>
            )}

            <div className="p-3 mb-4" style={cardSoftStyle}>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={newBin.location}
                    onChange={(e) => setNewBin({ ...newBin, location: e.target.value })}
                    placeholder="Enter bin number, ex: 90"
                    required
                    style={inputStyle}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label>Max Length (in)</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxLength"
                    value={newBin.maxLength}
                    onChange={(e) => setNewBin({ ...newBin, maxLength: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                    style={inputStyle}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label>Max Width (in)</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxWidth"
                    value={newBin.maxWidth}
                    onChange={(e) => setNewBin({ ...newBin, maxWidth: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                    style={inputStyle}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label>Max Height (in)</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxHeight"
                    value={newBin.maxHeight}
                    onChange={(e) => setNewBin({ ...newBin, maxHeight: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                    style={inputStyle}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label>Max Weight (lb)</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxWeight"
                    value={newBin.maxWeight}
                    onChange={(e) => setNewBin({ ...newBin, maxWeight: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                    style={inputStyle}
                  />
                </Col>

                <Col md={1}>
                  <Form.Label>Units</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxUnits"
                    value={newBin.maxUnits}
                    onChange={(e) => setNewBin({ ...newBin, maxUnits: e.target.value })}
                    min="0"
                    step="1"
                    required
                    style={inputStyle}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-3">
                {selectedBinForEdit && (
                  <Button
                    variant="outline-light"
                    onClick={() => {
                      setSelectedBinForEdit(null);
                      setNewBin({
                        location: "",
                        maxLength: "",
                        maxWidth: "",
                        maxHeight: "",
                        maxWeight: "",
                        maxUnits: "",
                      });
                    }}
                  >
                    Clear Edit
                  </Button>
                )}

                <Button variant="primary" onClick={handleSaveBinConfig}>
                  {selectedBinForEdit ? "Save Changes" : "Add Bin"}
                </Button>
              </div>
            </div>

            <h5 className="mb-3" style={{ color: "#f8fafc", fontWeight: 900 }}>
              Existing Bins
            </h5>

            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              <Table hover responsive size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Max Dimensions</th>
                    <th>Max Weight</th>
                    <th>Max Units</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {bins.length > 0 ? (
                    bins.map((bin) => (
                      <tr key={bin.id}>
                        <td>
                          <strong style={{ color: "#f8fafc" }}>{bin.location}</strong>
                        </td>
                        <td>{`${bin.maxLength} x ${bin.maxWidth} x ${bin.maxHeight}`}</td>
                        <td>{bin.maxWeight}</td>
                        <td>{bin.maxUnits}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button variant="warning" size="sm" onClick={() => handleEditBin(bin)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteBin(bin.id)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4" style={mutedStyle}>
                        No bins available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBinManageModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={barcodeModalOpen}
          onHide={() => setBarcodeModalOpen(false)}
          centered
          className="inventory-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Barcode for SKU: {currentSku}</Modal.Title>
          </Modal.Header>

          <Modal.Body className="text-center">
            {barcodeImage ? (
              <img
                src={getImageUrl(barcodeImage)}
                alt={`Barcode for ${currentSku}`}
                style={{
                  width: "100%",
                  maxWidth: 340,
                  height: "auto",
                  background: "#ffffff",
                  borderRadius: 16,
                  padding: 12,
                }}
              />
            ) : (
              <div className="p-4">
                <Spinner animation="border" variant="info" />
                <div className="mt-2" style={mutedStyle}>
                  Loading barcode...
                </div>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setBarcodeModalOpen(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={printBarcode}>
              <FaPrint className="me-2" />
              Print
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showSyncModal}
          onHide={() => setShowSyncModal(false)}
          centered
          className="inventory-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Sync Status for {syncDetails?.sku}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {syncDetails ? (
              <>
                <div className="p-3 mb-3" style={cardSoftStyle}>
                  <MiniMetric label="Stock" value={syncDetails.stock} color="#38bdf8" />
                </div>

                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <MiniMetric label="Shopify" value={syncDetails.statuses.shopify} color="#86efac" />
                    <div className="mt-1" style={{ ...mutedStyle, fontSize: 12 }}>
                      Last Sync: {syncDetails.lastSync.shopify || "Never"}
                    </div>
                  </Col>

                  <Col md={4}>
                    <MiniMetric label="eBay" value={syncDetails.statuses.ebay} color="#93c5fd" />
                    <div className="mt-1" style={{ ...mutedStyle, fontSize: 12 }}>
                      Last Sync: {syncDetails.lastSync.ebay || "Never"}
                    </div>
                  </Col>

                  <Col md={4}>
                    <MiniMetric label="Walmart" value={syncDetails.statuses.walmart} color="#fbbf24" />
                    <div className="mt-1" style={{ ...mutedStyle, fontSize: 12 }}>
                      Last Sync: {syncDetails.lastSync.walmart || "Never"}
                    </div>
                  </Col>
                </Row>

                <div
                  className="p-3"
                  style={{
                    borderRadius: 16,
                    background: "rgba(59,130,246,0.10)",
                    border: "1px solid rgba(59,130,246,0.20)",
                  }}
                >
                  <div style={{ color: "#94a3b8", fontSize: 12 }}>Overall Status</div>
                  <div style={{ color: "#f8fafc", fontWeight: 900 }}>
                    {syncDetails.overallStatus}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center p-4">
                <Spinner animation="border" variant="info" />
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSyncModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>


        <Modal
  show={showProductBinsModal}
  onHide={() => setShowProductBinsModal(false)}
  centered
  className="inventory-modal"
>
  <Modal.Header closeButton>
    <Modal.Title>
      Bins for {selectedProductForBins?.sku || "Product"}
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {selectedProductForBins && (
      <div className="mb-3" style={cardSoftStyle}>
        <div className="p-3">
          <div style={{ color: "#94a3b8", fontSize: 12, fontWeight: 800 }}>
            SKU
          </div>
          <div style={{ color: "#f8fafc", fontWeight: 900, fontSize: 18 }}>
            {selectedProductForBins.sku || "N/A"}
          </div>

          {selectedProductForBins.name && (
            <div style={{ color: "#cbd5e1", fontSize: 13, marginTop: 4 }}>
              {selectedProductForBins.name}
            </div>
          )}
        </div>
      </div>
    )}

    {productBinsLoading ? (
      <div className="text-center p-4">
        <Spinner animation="border" variant="info" />
        <div className="mt-2" style={mutedStyle}>
          Loading bins...
        </div>
      </div>
    ) : productBinsError ? (
      <Alert
        style={{
          background: "rgba(127,29,29,0.70)",
          border: "1px solid rgba(248,113,113,0.35)",
          color: "#fee2e2",
          borderRadius: 16,
        }}
      >
        {productBinsError}
      </Alert>
    ) : selectedProductBins.length > 0 ? (
      <div className="d-flex flex-column gap-2">
        {selectedProductBins.map((bin, index) => (
          <div
            key={`${bin.bin_id || bin.location || "bin"}-${index}`}
            className="d-flex justify-content-between align-items-center gap-3"
            style={{
              padding: "12px 14px",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.78)",
              border: "1px solid rgba(148, 163, 184, 0.16)",
            }}
          >
            <div>
              <div style={{ color: "#f8fafc", fontWeight: 900 }}>
                Bin {bin.location || bin.name || bin.bin_id || "Unknown"}
              </div>

              <div style={{ color: "#94a3b8", fontSize: 12 }}>
                ID: {bin.bin_id || "N/A"}
                {bin.barcode ? ` · Barcode: ${bin.barcode}` : ""}
              </div>
            </div>

            <Badge
              bg=""
              style={{
                background: "rgba(34,197,94,0.16)",
                color: "#86efac",
                border: "1px solid rgba(34,197,94,0.28)",
                borderRadius: 999,
                padding: "8px 12px",
                fontSize: 13,
              }}
            >
              Qty {cleanNumber(bin.quantity).toLocaleString()}
            </Badge>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center p-4" style={mutedStyle}>
        No bins found for this product.
      </div>
    )}
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowProductBinsModal(false)}>
      Close
    </Button>

    {selectedProductForBins?.sku && (
      <Button
        variant="warning"
        onClick={() => {
          setShowProductBinsModal(false);
          handleEditClick(selectedProductForBins.sku);
        }}
      >
        <FaEdit className="me-1" />
        Edit Product
      </Button>
    )}
  </Modal.Footer>
</Modal>
      </Container>
    </div>
  );
}

export default Inventory;